/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * LanguageSwitcher — Dropdown to switch app language
 * Works in header, settings page, or footer
 */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, LANGUAGES_ENABLED } from '@/i18n';

const LanguageSwitcher = ({ variant = 'dropdown', className = '' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = supportedLanguages.find(l => l.code === i18n.language) || supportedLanguages[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // English-only launch: hide the switcher entirely so users can't pick a
  // partially-translated language. The early return sits AFTER hooks so we
  // don't violate the Rules of Hooks; the flag is a module-level constant
  // so its value is stable across every render of this component.
  if (!LANGUAGES_ENABLED) return null;

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('vowsera-lang', code);
    document.documentElement.lang = code;
    setOpen(false);
  };

  // Compact inline variant (for header/footer)
  if (variant === 'inline') {
    return (
      <div ref={ref} className={`d-inline-block position-relative ${className}`}>
        <button onClick={() => setOpen(!open)} className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: 20 }}>
          <span>{current.flag}</span>
          <span>{current.code.toUpperCase()}</span>
          <i className="bi bi-chevron-down" style={{ fontSize: '0.55rem' }} />
        </button>
        {open && (
          <div style={{ position: 'absolute', top: '100%', right: 0, minWidth: 180, background: '#fff', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 9999, marginTop: 4, overflow: 'hidden' }}>
            {supportedLanguages.map(lang => (
              <button key={lang.code} onClick={() => changeLanguage(lang.code)}
                className="d-flex align-items-center gap-2 w-100 border-0 bg-transparent px-3 py-2"
                style={{ fontSize: '0.8rem', cursor: 'pointer', background: lang.code === current.code ? '#F8F4FF' : 'transparent', transition: 'background 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F8F4FF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = lang.code === current.code ? '#F8F4FF' : 'transparent'; }}>
                <span>{lang.flag}</span>
                <span className="fw-medium">{lang.nativeName}</span>
                <small className="text-muted ms-auto">{lang.name}</small>
                {lang.code === current.code && <i className="bi bi-check2 text-primary ms-1" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full dropdown variant (for settings page)
  return (
    <div className={className}>
      <label className="form-label fw-medium small">
        <i className="bi bi-translate me-1" />Language / भाषा
      </label>
      <div className="d-flex flex-wrap gap-2">
        {supportedLanguages.map(lang => (
          <button key={lang.code} onClick={() => changeLanguage(lang.code)}
            className={`btn btn-sm ${lang.code === current.code ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ borderRadius: 20, padding: '6px 16px', fontSize: '0.8rem' }}>
            <span className="me-1">{lang.flag}</span>
            {lang.nativeName}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
